/*
 * The constants both heads are built from.
 *
 * WHY THEY LIVE TOGETHER, AND HERE. Every public page's head is written twice:
 * once into the file by src/scripts/injectHeadTags.js, for crawlers that run no
 * JavaScript, and once into the DOM by src/scripts/publicHead.js when the app
 * mounts. Google reads both. Any value the two disagree on is a page describing
 * itself two different ways depending on who fetched it, so the values they
 * share are declared once, in a module plain node and webpack can both load
 * (CommonJS, same reason as src/data/siteOrigin.js -- the build scripts use
 * node's own require() and package.json has no "type": "module").
 *
 * A SERVED PATH, NOT A WEBPACK IMPORT. public/ is copied into dist/ verbatim,
 * so the card's URL survives an edit of the image. Bundling it from src/assets
 * (where it lived until unit 3) stamped a content hash into the filename, so
 * every edit changed the URL and every crawler cache still holding a preview
 * card pointed at a 404 -- and the build-time injector cannot resolve a webpack
 * hash at all.
 */

const SOCIAL_CARD_PATH = "/img/social-card.png";

// Daily Play's own 1200x630 card, advertised by the public share page at
// /play. A static image rather than a per-result one: the page is one static
// file for every share id (the id travels in the query string), so a crawler
// can only ever be shown one image for it, and that image must not claim any
// particular learner's result.
const DAILY_PLAY_CARD_PATH = "/img/daily-play-card.png";

// The brand, as it appears on the preview card next to the headline. A constant
// rather than a locale key for the same reason src/scripts/pageTitle.js keeps
// one: it is a proper noun, and a key would ship 31 byte-identical values that
// the translation parity gate then reports as untranslated copy.
const SITE_NAME = "ArticuLearn";

/*
 * The language of the content this URL advertises to a sharer, which is not the
 * same thing as the language of the interface the reader is using.
 *
 * One URL serves all 31 locales and the shareable copy is the English fallback
 * (per-locale head content is an explicit non-goal in the design spec), so this
 * is en_US on both sides even when the reader's own tab is in Japanese.
 */
const OG_LOCALE = "en_US";

// The only Twitter card type that renders a 1200x630 image at full width; the
// default "summary" shows a small square thumbnail instead.
const TWITTER_CARD = "summary_large_image";

/*
 * Image dimensions, opt-in, because a WRONG one is worse than a missing one: a
 * crawler that lays a card out from these numbers and then downloads a
 * differently-shaped image renders a stretched or letterboxed preview. So this
 * map holds only images whose size is part of their contract. The social card
 * is 1200x630 because that is the ratio it was drawn to. The about-us hero and
 * the blog heroes are editorial images that get swapped without ceremony, so
 * they get no dimensions rather than dimensions that go stale silently.
 */
const IMAGE_DIMENSIONS = {
  [SOCIAL_CARD_PATH]: { width: 1200, height: 630 },
  [DAILY_PLAY_CARD_PATH]: { width: 1200, height: 630 },
};

/** The declared size of an image, or undefined when we do not claim to know. */
function imageDimensions(imagePath) {
  return IMAGE_DIMENSIONS[imagePath];
}

/**
 * Head copy as a single line.
 *
 * Meta content keeps newlines and runs of spaces verbatim, and both reach the
 * preview card: one blog description ends in a space today, and a locale value
 * that wrapped onto two lines would hand crawlers a multi-line description.
 * None of that is meaningful text, so it is normalized on the way out rather
 * than policed at every source.
 *
 * Shared by both heads for a reason beyond tidiness: normalizing on only one
 * side means the served file and the hydrated DOM carry visibly different
 * content for the same tag, which is the exact class of disagreement this whole
 * unit exists to remove.
 */
function normalizeHeadCopy(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

module.exports = {
  SOCIAL_CARD_PATH,
  DAILY_PLAY_CARD_PATH,
  SITE_NAME,
  OG_LOCALE,
  TWITTER_CARD,
  IMAGE_DIMENSIONS,
  imageDimensions,
  normalizeHeadCopy,
};
